/**
 * A single icon-only button for the workspace header toolbar.
 *
 * Icon-only: the label surfaces on hover AND focus via the framework styled
 * {@link Tooltip} (instant, themed, keyboard-focus-aware) — NOT the delayed,
 * unstyled, touch-blind native `title` attribute (the IDE1 fix) — and is exposed
 * to assistive tech via `aria-label`. Square 34px hit target on desktop that
 * grows to 44px on phones (the Apple/Material/WCAG-AAA touch bar) via
 * `.mol-toolbar-icon-btn` in Workspace.css. The glyph always renders at
 * {@link TOOLBAR_ICON_SIZE} so every toolbar control — including the theme
 * toggle, which renders THROUGH this component — is exactly the same size.
 * Active state uses a single primary-tinted accent — deliberately one accent for
 * the whole toolbar rather than a per-button rainbow, which reads cleaner/calmer.
 *
 * Hover/active visuals are managed locally so the toolbar config stays a flat
 * list of descriptors instead of ~50 lines of inline handlers per button.
 */

import { useState } from 'react'

import type { IconName } from '@molecule/app-icons'
import { Tooltip } from '@molecule/app-ui-react'

import { Icon } from './Icon.js'

/**
 * The glyph size for EVERY toolbar control. Exported so sibling controls that
 * render through this button (the theme toggle) stay pixel-consistent with the
 * rest of the toolbar and a size-desync regression is catchable in a test.
 */
export const TOOLBAR_ICON_SIZE = 18

/** Props for a toolbar icon button. */
export interface ToolbarIconButtonProps {
  /** Icon set name (resolved via `<Icon name>`). */
  icon: IconName
  /** Human label — shown in the styled {@link Tooltip} and set as `aria-label`. */
  label: string
  /** Whether the button's panel is currently open (drives the accent state). */
  active?: boolean
  /** Stable automation id, rendered as `data-mol-id` (e.g. `topbar-logs`). */
  molId: string
  /** Click handler (typically toggles the associated panel). */
  onClick: () => void
  /**
   * Marks the action as not-yet-functional. The button is dimmed and gets a
   * primary-token corner dot, and its {@link Tooltip} shows {@link comingSoonLabel}
   * in place of the plain {@link label}, so it stays visibly distinct from a
   * working control (the IDE9 fix: a Team button identical to working ones
   * advertised Team as functional). The click still fires (the panel explains
   * the deferral). Every button — working or coming-soon — uses the same styled
   * Tooltip; only the dim + badge + tooltip text differ.
   */
  comingSoon?: boolean
  /**
   * Tooltip + accessible label shown when {@link comingSoon} (already
   * translated by the caller, e.g. "Team collaboration — coming soon").
   * Falls back to {@link label} when omitted.
   */
  comingSoonLabel?: string
  /**
   * Renders the button visible but inert — dimmed, `cursor: not-allowed`, no
   * hover affordance, and the click is suppressed. Used while the sandbox is
   * still booting so the toolbar is visible the moment discovery ends (E2)
   * without firing actions that genuinely need a running sandbox. The styled
   * {@link Tooltip} still surfaces the label so the control stays identifiable.
   */
  disabled?: boolean
  /**
   * Count for a small corner notification badge (e.g. how many detected env
   * vars still need a value on the Environment button). `0`/`undefined`
   * renders NO badge. Displayed capped at "9+". Same corner treatment as the
   * comingSoon dot (absolute corner span + surface ring), but tinted by
   * {@link badgeTone} and carrying the readable tiny count.
   */
  badgeCount?: number
  /** Badge tint token: `warning` (default) or `error`. */
  badgeTone?: 'warning' | 'error'
  /**
   * Renders this button as part of a segmented control (the Preview/Code
   * view-group). Segmented geometry — designed against `.mol-toolbar-view-group`'s
   * `padding: 0` + `border-radius: 9px` + `1px` border so the SELECTED button's
   * border overlaps the wrapper's border PERFECTLY (corners coincide), instead of
   * the old mismatched 7px-vs-9px corners:
   * - `borderRadius: 9` matches the wrapper's OUTER radius (9px), NOT the inner
   *   8px. Both segmented buttons carry `margin: -1px`, which cancels the
   *   wrapper's 1px border on every side, so the button's `border-box` outer edge
   *   lines up with the wrapper's OUTER edge. A 9px outer radius minus the 1px
   *   border gives an 8px inner radius — i.e. the button's 1px border occupies the
   *   SAME 8→9px annulus as the wrapper's own 1px border, painting right on top of
   *   it (no doubled border, no gap). BOTH buttons (not just the active one) take
   *   the `-1` margin so the cross-axis geometry is symmetric — otherwise the
   *   inactive button (no margin) would set the wrapper's content height and stop
   *   the active button's border-box from reaching the wrapper's outer top/bottom
   *   edges, breaking the overlap on those sides.
   * - When active, the border uses the wrapper's EXACT accent (`--mol-color-primary`
   *   at 35%, matching `.mol-toolbar-view-group`); when inactive it's a transparent
   *   1px border in the same place, so toggling is a pure color flip with no layout
   *   shift. `boxSizing: border-box` keeps the 34px footprint stable.
   * Standalone toolbar buttons (the default, `false`) keep the lone 40% accent, a
   * 7px radius, and no margin — ONLY the two view-group buttons get the segmented
   * treatment, so the rest of the toolbar is unchanged.
   */
  segmented?: boolean
}

/**
 * Renders one icon-only toolbar button with a tooltip, active accent, and hover
 * affordance.
 * @param root0 - Component props.
 * @param root0.icon - Icon set name.
 * @param root0.label - Tooltip + accessible label.
 * @param root0.active - Active (panel-open) state.
 * @param root0.molId - `data-mol-id` value.
 * @param root0.onClick - Click handler.
 * @returns The rendered toolbar button.
 */
export function ToolbarIconButton({
  icon,
  label,
  active = false,
  molId,
  onClick,
  comingSoon = false,
  comingSoonLabel,
  disabled = false,
  badgeCount,
  badgeTone = 'warning',
  segmented = false,
}: ToolbarIconButtonProps) {
  const [hover, setHover] = useState(false)
  // A disabled control never reflects hover/active visuals — it is inert.
  const showHover = hover && !disabled
  const showActive = active && !disabled

  const background = showActive
    ? 'color-mix(in srgb, var(--mol-color-primary, #6366f1) 16%, transparent)'
    : // A SEGMENTED button must NOT paint a hover background: with its -1px margin the bg
      // would extend over and cover the group container's border. On hover it only shifts
      // its icon color (below). A standalone button keeps the surface-tint hover.
      showHover && !segmented
      ? 'var(--color-surface)'
      : 'transparent'
  const color = showActive
    ? 'var(--mol-color-primary, #6366f1)'
    : showHover
      ? 'var(--color-foreground-secondary)'
      : 'var(--mol-color-text-secondary, #aaaaaa)'

  const accessibleLabel = comingSoon && comingSoonLabel ? comingSoonLabel : label

  const button = (
    <button
      type="button"
      // mol-bespoke-button: THE icon-only workspace toolbar button, no label —
      // a flat 34px glyph square (44px on touch) that also composes into a
      // segmented group, where the -1px border-box pull makes the selected
      // button's border coincide with the group wrapper's. No CVA size tier
      // expresses that, and a button box on every toolbar icon would fill the
      // bar with frames. Every colour is a theme token.
      //
      // Sizing lives in CSS (`.mol-toolbar-icon-btn` in Workspace.css) — not
      // inline — so the mobile breakpoint can bump the 34px square to a ≥44px
      // touch target (inline styles would otherwise beat the media query).
      className="mol-toolbar-icon-btn"
      data-mol-id={molId}
      aria-label={accessibleLabel}
      aria-pressed={showActive}
      disabled={disabled}
      aria-disabled={disabled}
      // No native `title`: every toolbar button surfaces its label through the
      // framework styled <Tooltip> wrapper below (instant + themed + focus-aware
      // + touch-capable), which the delayed/unstyled native tooltip is not.
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => !disabled && setHover(true)}
      onMouseLeave={() => !disabled && setHover(false)}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        // Segmented buttons match the wrapper's OUTER radius (9px) so the selected
        // button's rounded corners coincide with `.mol-toolbar-view-group`'s;
        // standalone toolbar buttons keep the 7px radius.
        borderRadius: segmented ? 9 : 7,
        // Segmented buttons size via border-box (in both states) so their 34px
        // footprint stays stable when the -1 margin pulls the border flush.
        boxSizing: segmented ? 'border-box' : undefined,
        // Active accent: a segmented button matches the wrapper's EXACT 35% accent
        // (`.mol-toolbar-view-group`), a standalone button keeps its lone 40%.
        border: showActive
          ? `1px solid color-mix(in srgb, var(--mol-color-primary, #6366f1) ${
              segmented ? 35 : 40
            }%, transparent)`
          : '1px solid transparent',
        // Pull BOTH segmented buttons' border-box out by 1px (cancelling the
        // wrapper's 1px border on every side) so the SELECTED button's 1px border
        // lands on the SAME 8→9px annulus as the wrapper's border — a perfect
        // overlap, not a doubled border. Both (not just the active one) so the
        // cross-axis geometry is symmetric and the active border-box can reach the
        // wrapper's outer top/bottom edges. Standalone buttons never shift.
        margin: segmented ? -1 : undefined,
        background,
        color,
        cursor: disabled ? 'not-allowed' : 'pointer',
        // Disabled wins: dim hard + inert. Otherwise dim a coming-soon control so
        // it never reads as a working button; lift slightly on hover so it still
        // feels interactive.
        opacity: disabled ? 0.4 : comingSoon ? (hover ? 0.85 : 0.55) : 1,
        transition: 'background 100ms, border-color 100ms, color 100ms, opacity 100ms',
      }}
    >
      <Icon name={icon} size={TOOLBAR_ICON_SIZE} />
      {typeof badgeCount === 'number' && badgeCount > 0 && (
        // Corner COUNT badge (mirrors the comingSoon dot's absolute corner +
        // surface ring). Colors are theme tokens only — no hardcoded hex; the
        // surface token keeps the count readable on the solid tone in both
        // themes, same as the ring.
        <span
          data-mol-id={`${molId}-badge`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -4,
            right: -4,
            minWidth: 15,
            height: 15,
            // Optical vertical centering. Flex centers the LINE box, but a digit
            // only inks from the baseline up — the descender space below it is
            // empty, so the glyph lands above the circle's center and reads as
            // "a couple of pixels too high". Measured in-browser at this font
            // stack (system-ui): 1px high at fontSize 10. A 2px top pad shifts
            // the centered line box down by exactly half that padding, i.e. 1px,
            // which lands the ink on the true center.
            padding: '2px 3px 0',
            boxSizing: 'border-box',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            lineHeight: 1,
            background: badgeTone === 'error' ? 'var(--color-error)' : 'var(--color-warning)',
            color: 'var(--color-surface)',
            boxShadow: '0 0 0 2px var(--color-surface)',
          }}
        >
          {badgeCount > 9 ? '9+' : badgeCount}
        </span>
      )}
      {comingSoon && (
        // Primary-token corner dot marking the deferral. Absolute offset + the
        // surface-token ring are things ClassMap can't express, but every color
        // is a theme token (no hardcoded hex).
        <span
          data-mol-id={`${molId}-coming-soon-badge`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -1,
            right: -1,
            width: 7,
            height: 7,
            borderRadius: 999,
            background: 'var(--color-primary)',
            boxShadow: '0 0 0 2px var(--color-surface)',
          }}
        />
      )}
    </button>
  )

  // Every toolbar button — working or coming-soon — gets the same styled tooltip
  // (placement below the top-of-screen toolbar). The content is the accessible
  // label, so a coming-soon button shows its deferral copy while siblings show
  // their plain label.
  return (
    <Tooltip content={accessibleLabel} placement="bottom">
      {button}
    </Tooltip>
  )
}
